import { pgaComTld } from "../../../utils"
import { ADDITIONAL_REQUIREMENTS } from "./constants/additionalRequirements"
import { REQUIREMENT } from "./constants/levels"
import { CHANGE_FORM_URLS } from "./constants/externalUrls"
import { MEMBER_APPLICATIONS_URL } from "../../../../lib/ptmApplicationsRedirect"

export const CHECKLIST_KEY = {
  EXPERIENCE_CREDITS: "experience_credits",
  EMPLOYMENT: "employment",
  PAT: REQUIREMENT.PLAYING_ABILITY_TEST,
  AGE: "age",
  GOOD_STANDING: "good_standing",
  ACCEPTABLE_PROGRESS: "acceptable_progress",
}

export const ENROLLMENT_LOCKED_FALLBACK =
  "Complete all remaining requirements before you can apply for Membership."

export const JOURNEY_STANDING_SUSPENSION_CODES = ["GPTP", "NPDU", "COEV"]

const DEFAULT_CREDIT_THRESHOLD = 28

const isComplete = criterion => Boolean(criterion?.complete)
const isExplicitlyIncomplete = criterion => criterion?.complete === false
const hasExpiredProgressWindow = criterion => criterion?.expired === true

export const hasJourneyStandingSuspension = (suspensions = []) =>
  suspensions.some(suspension =>
    JOURNEY_STANDING_SUSPENSION_CODES.includes(suspension?.code),
  )

const formatList = items => {
  if (items.length === 1) return items[0]
  if (items.length === 2) return `${items[0]} and ${items[1]}`
  return `${items.slice(0, -1).join(", ")}, and ${items[items.length - 1]}`
}

export const membershipDuesUrl = () => `https://${pgaComTld}/member/dues`

const isEligibilityPatIncomplete = eligibility => {
  const pat = eligibility?.playingAbilityTest
  if (!pat) return false
  return !pat.complete || Boolean(pat.expired)
}

export const formatExperienceCreditsDescription = credits => {
  if (!credits) {
    return ADDITIONAL_REQUIREMENTS.EXPERIENCE_CREDITS.description
  }

  const total = credits.total ?? 0
  const threshold = credits.threshold ?? DEFAULT_CREDIT_THRESHOLD
  const education = credits.educationCredits
  const employment = credits.employmentCredits
  const hasSplit = education != null && employment != null
  const split = hasSplit
    ? ` (${education} education, ${employment} employment)`
    : ""

  if (credits.complete) {
    return `You have met the ${threshold}-credit requirement with ${total} credits${split}.`
  }

  return `You have ${total} of the ${threshold} credits you need${split}.`
}

export const buildMemberApplicationChecklist = ({
  eligibility,
  showPatWarning,
  suspensions = [],
}) => {
  const items = []
  const showPatRow =
    Boolean(showPatWarning) || isEligibilityPatIncomplete(eligibility)

  if (showPatRow) {
    items.push({
      key: CHECKLIST_KEY.PAT,
      complete: false,
      hasWarning: true,
      title: ADDITIONAL_REQUIREMENTS.PAT.title,
      description: isEligibilityPatIncomplete(eligibility)
        ? ADDITIONAL_REQUIREMENTS.PAT.incompleteDescription
        : ADDITIONAL_REQUIREMENTS.PAT.description,
      primaryLinkText: ADDITIONAL_REQUIREMENTS.PAT.primaryLinkText,
      primaryLinkKind: "pat-search",
      secondaryLinkText: ADDITIONAL_REQUIREMENTS.PAT.secondaryLinkText,
      secondaryLinkUrl: ADDITIONAL_REQUIREMENTS.PAT.secondaryLinkUrl,
    })
  }

  const credits = eligibility?.experienceCredits
  items.push({
    key: CHECKLIST_KEY.EXPERIENCE_CREDITS,
    complete: Boolean(credits?.complete),
    hasWarning: Boolean(credits) && !credits.complete,
    title: ADDITIONAL_REQUIREMENTS.EXPERIENCE_CREDITS.title,
    description: formatExperienceCreditsDescription(credits),
    secondaryLinkText: ADDITIONAL_REQUIREMENTS.EXPERIENCE_CREDITS.linkText,
    secondaryLinkUrl: ADDITIONAL_REQUIREMENTS.EXPERIENCE_CREDITS.linkUrl,
  })

  if (!eligibility) {
    return items
  }

  if (typeof eligibility.activelyEmployed?.complete === "boolean") {
    const employed = eligibility.activelyEmployed.complete === true
    items.push({
      key: CHECKLIST_KEY.EMPLOYMENT,
      complete: employed,
      hasWarning: !employed,
      title: ADDITIONAL_REQUIREMENTS.EMPLOYMENT.title,
      description: employed
        ? ADDITIONAL_REQUIREMENTS.EMPLOYMENT.completeDescription
        : ADDITIONAL_REQUIREMENTS.EMPLOYMENT.description,
      ...(employed
        ? {}
        : {
            primaryLinkText: ADDITIONAL_REQUIREMENTS.EMPLOYMENT.primaryLinkText,
            primaryLinkUrl: CHANGE_FORM_URLS.NEW_EMPLOYMENT,
          }),
    })
  }

  if (isExplicitlyIncomplete(eligibility.age)) {
    items.push({
      key: CHECKLIST_KEY.AGE,
      complete: false,
      hasWarning: true,
      title: ADDITIONAL_REQUIREMENTS.AGE.title,
      description: ADDITIONAL_REQUIREMENTS.AGE.description,
    })
  }

  if (
    isExplicitlyIncomplete(eligibility.goodStanding) &&
    !hasJourneyStandingSuspension(suspensions)
  ) {
    items.push({
      key: CHECKLIST_KEY.GOOD_STANDING,
      complete: false,
      hasWarning: true,
      title: ADDITIONAL_REQUIREMENTS.GOOD_STANDING.title,
      description: ADDITIONAL_REQUIREMENTS.GOOD_STANDING.description,
      primaryLinkText: ADDITIONAL_REQUIREMENTS.GOOD_STANDING.primaryLinkText,
      primaryLinkUrl: membershipDuesUrl(),
    })
  }

  if (hasExpiredProgressWindow(eligibility.acceptableProgress)) {
    items.push({
      key: CHECKLIST_KEY.ACCEPTABLE_PROGRESS,
      complete: false,
      hasWarning: true,
      title: ADDITIONAL_REQUIREMENTS.ACCEPTABLE_PROGRESS.title,
      description: ADDITIONAL_REQUIREMENTS.ACCEPTABLE_PROGRESS.description,
      primaryLinkText:
        ADDITIONAL_REQUIREMENTS.ACCEPTABLE_PROGRESS.primaryLinkText,
      primaryLinkUrl: MEMBER_APPLICATIONS_URL,
    })
  }

  return items
}

export const getEnrollmentLockedMessage = (
  eligibility,
  { suspensions } = {},
) => {
  if (!eligibility) {
    return ENROLLMENT_LOCKED_FALLBACK
  }

  const leftovers = []

  if (isEligibilityPatIncomplete(eligibility)) {
    leftovers.push("pass the PAT")
  }

  const credits = eligibility.experienceCredits
  if (credits && !credits.complete) {
    const total = credits.total ?? 0
    const threshold = credits.threshold ?? DEFAULT_CREDIT_THRESHOLD
    leftovers.push(`earn ${threshold} experience credits (you have ${total})`)
  }

  if (isExplicitlyIncomplete(eligibility.activelyEmployed)) {
    leftovers.push("update your eligible employment")
  }

  if (isExplicitlyIncomplete(eligibility.age)) {
    leftovers.push("confirm you are 18 or older")
  }

  if (
    isExplicitlyIncomplete(eligibility.goodStanding) &&
    !hasJourneyStandingSuspension(suspensions)
  ) {
    leftovers.push("pay your dues")
  }

  if (hasExpiredProgressWindow(eligibility.acceptableProgress)) {
    leftovers.push("reapply for the Path to Membership")
  }

  const leftoverList = formatList(leftovers)
  const needsLevel3 = !isComplete(eligibility.pgmLevels)

  if (leftovers.length === 0) {
    return "Complete the Level 3 requirements above before you can apply for Membership."
  }

  if (needsLevel3) {
    return `Complete the Level 3 requirements above and these remaining items in Additional Requirements before you can apply: ${leftoverList}.`
  }

  return `Complete these remaining items in Additional Requirements before you can apply: ${leftoverList}.`
}
